import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import CONFIG from "../../config";

export default function FeedbackModal({ open, onClose }) {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(5);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [hoveredStar, setHoveredStar] = useState(0);

  if (!open) return null;

  const submit = async () => {
    if (!message.trim()) return alert(t('modals.feedback.required'));

    await axios.post(`${CONFIG.API_URL}/api/feedback`, {
      name,
      email,
      message,
      rating
    });

    alert(t('modals.feedback.success'));
    setMessage("");
    setName("");
    setEmail("");
    setRating(5);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[40px] w-full max-w-2xl shadow-2xl transform transition-all animate-scale-in overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-primary to-indigo-600 p-10 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center">
                  <i className="fas fa-comment-dots text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-3xl font-black tracking-tight">{t('modals.feedback.title')}</h3>
                  <p className="text-white/80 text-sm font-medium mt-1">{t('modals.feedback.subtitle', "We'd love to hear from you!")}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-xl flex items-center justify-center transition-all hover:scale-110 active:scale-95"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-10">
          {/* Star Rating */}
          <div className="mb-8">
            <label className="block text-xs font-black uppercase tracking-widest text-gray-400 mb-4 ml-2">
              <i className="fas fa-star text-yellow-400 mr-2"></i>
              {t('modals.feedback.rating', 'Your Rating')}
            </label>
            <div className="flex gap-3 justify-center p-6 bg-gray-50 rounded-3xl">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  className="transition-all hover:scale-125 active:scale-110"
                >
                  <i
                    className={`text-5xl transition-colors ${star <= (hoveredStar || rating)
                      ? 'fas fa-star text-yellow-400'
                      : 'far fa-star text-gray-300'
                      }`}
                  ></i>
                </button>
              ))}
            </div>
            <p className="text-center mt-4 text-sm font-bold text-gray-600">
              {rating === 5 && `⭐ ${t('modals.feedback.ratings.excellent', 'Excellent!')}`}
              {rating === 4 && `😊 ${t('modals.feedback.ratings.great', 'Great!')}`}
              {rating === 3 && `👍 ${t('modals.feedback.ratings.good', 'Good')}`}
              {rating === 2 && `😐 ${t('modals.feedback.ratings.fair', 'Fair')}`}
              {rating === 1 && `😞 ${t('modals.feedback.ratings.poor', 'Poor')}`}
            </p>
          </div>

          {/* Form Fields */}
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-widest text-gray-400 ml-4">
                  <i className="fas fa-user mr-2"></i>
                  {t('modals.feedback.name_opt', 'Name (Optional)')}
                </label>
                <input
                  type="text"
                  className="w-full bg-gray-50 border-none p-4 rounded-2xl focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium placeholder:text-gray-300 outline-none"
                  placeholder={t('contact.placeholders.name', 'John Doe')}
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-widest text-gray-400 ml-4">
                  <i className="fas fa-envelope mr-2"></i>
                  {t('modals.feedback.email_opt', 'Email (Optional)')}
                </label>
                <input
                  type="email"
                  className="w-full bg-gray-50 border-none p-4 rounded-2xl focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium placeholder:text-gray-300 outline-none"
                  placeholder={t('contact.placeholders.email', 'john@example.com')}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-black uppercase tracking-widest text-gray-400 ml-4">
                <i className="fas fa-message mr-2"></i>
                {t('modals.feedback.message', 'Your Feedback')}
              </label>
              <textarea
                className="w-full bg-gray-50 border-none p-5 rounded-2xl focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium placeholder:text-gray-300 outline-none resize-none"
                rows={6}
                placeholder={t('modals.feedback.placeholder', 'Tell us what you think...')}
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 mt-10">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all hover:scale-[1.02] active:scale-95"
            >
              <i className="fas fa-times mr-2"></i>
              {t('modals.feedback.cancel', 'Cancel')}
            </button>
            <button
              onClick={submit}
              className="flex-1 bg-gradient-to-r from-brand-primary to-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-brand-primary/20 hover:scale-[1.02] active:scale-95 transition-all"
            >
              <i className="fas fa-paper-plane mr-2"></i>
              {t('modals.feedback.submit', 'Send Feedback')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
